import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  User,
  Mail,
  Award,
  Link as LinkIcon,
  Copy,
  Check,
  TrendingUp,
  Users,
  DollarSign,
  Lock,
  CreditCard,
  Building,
  Phone,
  Eye,
  EyeOff,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getTierColorClass } from "@/config/tiers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Form states
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingPayout, setSavingPayout] = useState(false);
  
  // Account Info fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [partnerType, setPartnerType] = useState("");
  
  // Password fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  
  // Payout fields
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [bankRouting, setBankRouting] = useState("");
  const [bankAccount, setBankAccount] = useState("");

  useEffect(() => {
    document.title = "Account Settings | GeniusList Partners";
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`, { withCredentials: true });
      const data = response.data;
      setProfile(data);
      
      // Populate form fields
      setFirstName(data.first_name || "");
      setLastName(data.last_name || "");
      setCompany(data.company || "");
      setPhone(data.phone || "");
      setPartnerType(data.partner_type || "influencer");
      setPaymentMethod(data.payment_method || "paypal");
      setPaypalEmail(data.paypal_email || "");
      setBankRouting(data.bank_routing || "");
      setBankAccount(data.bank_account || "");
    } catch (error) {
      console.error("Profile fetch error:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAccountInfo = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/profile`,
        { 
          first_name: firstName,
          last_name: lastName,
          company,
          phone,
          partner_type: partnerType
        },
        { withCredentials: true }
      );
      setProfile(response.data);
      toast.success("Account information updated successfully");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save account information");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayoutDetails = async () => {
    setSavingPayout(true);
    try {
      const response = await axios.put(
        `${API}/profile`,
        { 
          payment_method: paymentMethod,
          paypal_email: paypalEmail,
          bank_routing: bankRouting,
          bank_account: bankAccount
        },
        { withCredentials: true }
      );
      setProfile(response.data);
      toast.success("Payout details updated successfully");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save payout details");
    } finally {
      setSavingPayout(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError("");
    
    // Validation
    if (profile?.has_password && !currentPassword) {
      setPasswordError("Current password is required");
      return;
    }
    
    if (!newPassword) {
      setPasswordError("New password is required");
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }
    
    setSavingPassword(true);
    try {
      await axios.put(
        `${API}/account/password`,
        { 
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        },
        { withCredentials: true }
      );
      
      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setProfile(prev => ({ ...prev, has_password: true }));
      toast.success("Password updated successfully");
    } catch (error) {
      console.error("Password change error:", error);
      const message = error.response?.data?.detail || "Failed to change password";
      setPasswordError(message);
      toast.error(message);
    } finally {
      setSavingPassword(false);
    }
  };

  const copyReferralLink = async () => {
    if (profile?.referral_link) {
      await navigator.clipboard.writeText(profile.referral_link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTierColor = (tier) => {
    return getTierColorClass(tier);
  };

  // Check if account info has changes
  const hasAccountChanges = 
    firstName !== (profile?.first_name || "") ||
    lastName !== (profile?.last_name || "") ||
    company !== (profile?.company || "") ||
    phone !== (profile?.phone || "") ||
    partnerType !== (profile?.partner_type || "influencer");

  // Check if payout has changes
  const hasPayoutChanges = 
    paymentMethod !== (profile?.payment_method || "paypal") ||
    paypalEmail !== (profile?.paypal_email || "") ||
    bankRouting !== (profile?.bank_routing || "") ||
    bankAccount !== (profile?.bank_account || "");

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="bg-white border-slate-100">
          <CardContent className="p-8">
            <div className="flex items-center gap-6">
              <Skeleton className="w-24 h-24 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="grid lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-white border-slate-100">
              <CardContent className="p-6">
                <Skeleton className="h-6 w-40 mb-4" />
                <Skeleton className="h-10 w-full mb-3" />
                <Skeleton className="h-10 w-full mb-3" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: "Total Referrals",
      value: profile?.total_referrals || 0,
      icon: Users,
      color: "text-purple-600 bg-purple-50"
    },
    {
      label: "Active Referrals",
      value: profile?.active_referrals || 0,
      icon: TrendingUp,
      color: "text-emerald-600 bg-emerald-50"
    },
    {
      label: "Total Earnings",
      value: `$${(profile?.total_earnings || 0).toLocaleString()}`,
      icon: DollarSign,
      color: "text-blue-600 bg-blue-50"
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Profile Header */}
      <Card className="bg-white border-slate-100 shadow-sm overflow-hidden">
        <div className="h-32 gradient-btn"></div>
        <CardContent className="p-6 -mt-16">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
              <AvatarImage src={profile?.picture} alt={profile?.name} />
              <AvatarFallback className="text-2xl bg-purple-100 text-purple-600">
                {profile?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900">{profile?.name}</h1>
                <Badge className={`${getTierColor(profile?.tier)} capitalize`}>
                  {profile?.tier}
                </Badge>
              </div>
              <p className="text-slate-500 mt-1">{profile?.email}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Commission Rate</p>
              <p className="text-2xl font-bold gradient-text">{profile?.commission_rate}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-3 gap-4">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Card key={idx} className="bg-white border-slate-100 shadow-sm" data-testid={`stat-card-${idx}`}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className={`p-3 rounded-xl ${stat.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Settings Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Account Information */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Account Information
            </CardTitle>
            <CardDescription>Update your personal details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                  First Name
                </Label>
                <Input 
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  className="mt-1"
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-sm font-medium text-slate-700">
                  Last Name
                </Label>
                <Input 
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className="mt-1"
                  data-testid="last-name-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="company" className="text-sm font-medium text-slate-700">
                Company Name <span className="text-slate-400">(optional)</span>
              </Label>
              <div className="relative mt-1">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Your company"
                  className="pl-10"
                  data-testid="company-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email Address
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  id="email"
                  value={profile?.email || ""} 
                  readOnly 
                  disabled
                  className="bg-slate-50 pl-10 text-slate-500"
                  data-testid="profile-email"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Email cannot be changed at this time</p>
            </div>
            
            <div>
              <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                Phone Number
              </Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="pl-10"
                  data-testid="phone-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Partner Type
              </Label>
              <Select value={partnerType} onValueChange={setPartnerType}>
                <SelectTrigger className="mt-1" data-testid="partner-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="influencer">Influencer</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="affiliate">Affiliate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={handleSaveAccountInfo}
              disabled={saving || !hasAccountChanges}
              className="w-full gradient-btn text-white"
              data-testid="save-account-btn"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "Save Account Info"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Partner Details */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Partner Details
            </CardTitle>
            <CardDescription>Your partner program information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Partner Code
              </Label>
              <Input 
                value={profile?.partner_code || ""} 
                readOnly 
                className="bg-slate-50 font-mono font-bold text-purple-600 mt-1"
                data-testid="partner-code"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Current Tier
              </Label>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg mt-1">
                <Badge className={`${getTierColor(profile?.tier)} capitalize text-base px-4 py-1`}>
                  {profile?.tier}
                </Badge>
                <span className="text-slate-600">
                  {profile?.commission_rate}% commission rate
                </span>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Referral Link
              </Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    value={profile?.referral_link || ""} 
                    readOnly 
                    className="bg-slate-50 pl-10 text-sm"
                    data-testid="profile-referral-link"
                  />
                </div>
                <Button 
                  onClick={copyReferralLink}
                  variant="outline"
                  size="icon"
                  data-testid="copy-referral-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Member Since
              </Label>
              <Input 
                value={profile?.created_at 
                  ? new Date(profile.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric"
                    })
                  : "-"
                } 
                readOnly 
                className="bg-slate-50 mt-1"
                data-testid="member-since"
              />
            </div>
          </CardContent>
        </Card>

        {/* Login & Security */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Lock className="w-5 h-5 text-rose-600" />
              Login & Security
            </CardTitle>
            <CardDescription>
              {profile?.has_password ? "Update your password" : "Set a password for your account"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {profile?.has_password && (
              <div>
                <Label htmlFor="currentPassword" className="text-sm font-medium text-slate-700">
                  Current Password
                </Label>
                <div className="relative mt-1">
                  <Input 
                    id="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700">
                New Password
              </Label>
              <div className="relative mt-1">
                <Input 
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Minimum 8 characters</p>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                Confirm New Password
              </Label>
              <Input 
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>
            
            {passwordError && (
              <div className="flex items-center gap-2 p-3 bg-rose-50 text-rose-700 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {passwordError}
              </div>
            )}
            
            <Button 
              onClick={handleChangePassword}
              disabled={savingPassword || (!newPassword && !confirmPassword)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="change-password-btn"
            >
              {savingPassword ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                profile?.has_password ? "Update Password" : "Set Password"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Payout Details */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Payout Details
            </CardTitle>
            <CardDescription>Configure how you receive payments</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-3 block">
                Payment Method
              </Label>
              <RadioGroup 
                value={paymentMethod} 
                onValueChange={setPaymentMethod}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="paypal" id="paypal" data-testid="paypal-radio" />
                  <Label htmlFor="paypal" className="cursor-pointer">PayPal</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="bank" id="bank" data-testid="bank-radio" />
                  <Label htmlFor="bank" className="cursor-pointer">Bank Transfer</Label>
                </div>
              </RadioGroup>
            </div>
            
            <Separator />
            
            {paymentMethod === "paypal" ? (
              <div>
                <Label htmlFor="paypalEmail" className="text-sm font-medium text-slate-700">
                  PayPal Email
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    id="paypalEmail"
                    type="email"
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    placeholder="your@paypal.email"
                    className="pl-10"
                    data-testid="paypal-email-input"
                  />
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="bankRouting" className="text-sm font-medium text-slate-700">
                    Routing Number
                  </Label>
                  <Input 
                    id="bankRouting"
                    value={bankRouting}
                    onChange={(e) => setBankRouting(e.target.value)}
                    placeholder="9 digit routing number"
                    className="mt-1"
                    maxLength={9}
                    data-testid="bank-routing-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bankAccount" className="text-sm font-medium text-slate-700">
                    Account Number
                  </Label>
                  <Input 
                    id="bankAccount"
                    value={bankAccount}
                    onChange={(e) => setBankAccount(e.target.value)}
                    placeholder="Account number"
                    className="mt-1"
                    data-testid="bank-account-input"
                  />
                </div>
              </>
            )}
            
            <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-xs text-amber-700">
                <strong>Note:</strong> Payout details are used for commission payments. 
                Ensure your information is accurate to avoid payment delays.
              </p>
            </div>
            
            <Button 
              onClick={handleSavePayoutDetails}
              disabled={savingPayout || !hasPayoutChanges}
              className="w-full gradient-btn text-white"
              data-testid="save-payout-btn"
            >
              {savingPayout ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "Save Payout Details"
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
